/**
 * NOS-132 M14: CRUD + reorder for event_ticket_types.
 */

import { useCallback, useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"
import type { Database } from "@/types/database"

export type EventTicketTypeRow = Database["public"]["Tables"]["event_ticket_types"]["Row"]
type EventTicketTypeInsert = Database["public"]["Tables"]["event_ticket_types"]["Insert"]
type EventTicketTypeUpdate = Database["public"]["Tables"]["event_ticket_types"]["Update"]

export interface SaveTicketTypeInput {
  id?: string
  event_id: string
  org_id: string
  name: string
  description: string | null
  price_cents: number
  capacity: number | null
  seats_per_ticket: number
  sort_order: number
  is_active: boolean
}

export function useTicketTypes(eventId: string | null, orgId: string | null) {
  const [ticketTypes, setTicketTypes] = useState<EventTicketTypeRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchTicketTypes = useCallback(async () => {
    if (!eventId || !orgId || eventId === "new") {
      setTicketTypes([])
      setLoading(false)
      return
    }
    setLoading(true)
    setError(null)
    const { data, error: fetchError } = await supabase
      .from("event_ticket_types")
      .select("*")
      .eq("event_id", eventId)
      .eq("org_id", orgId)
      .order("sort_order", { ascending: true })

    if (fetchError) {
      setError(fetchError.message)
      setTicketTypes([])
    } else {
      setTicketTypes((data ?? []) as EventTicketTypeRow[])
    }
    setLoading(false)
  }, [eventId, orgId])

  useEffect(() => {
    fetchTicketTypes()
  }, [fetchTicketTypes])

  const saveTicketType = useCallback(
    async (input: SaveTicketTypeInput): Promise<EventTicketTypeRow | null> => {
      if (!orgId) return null
      if (input.id) {
        const update: EventTicketTypeUpdate = {
          name: input.name,
          description: input.description,
          price_cents: input.price_cents,
          capacity: input.capacity,
          seats_per_ticket: input.seats_per_ticket,
          sort_order: input.sort_order,
          is_active: input.is_active,
        }
        const { data, error: upErr } = await supabase
          .from("event_ticket_types")
          .update(update)
          .eq("id", input.id)
          .eq("org_id", orgId)
          .select()
          .single()
        if (upErr) {
          setError(upErr.message)
          return null
        }
        await fetchTicketTypes()
        return data as EventTicketTypeRow
      }

      const insert: EventTicketTypeInsert = {
        event_id: input.event_id,
        org_id: input.org_id,
        name: input.name,
        description: input.description,
        price_cents: input.price_cents,
        capacity: input.capacity,
        seats_per_ticket: input.seats_per_ticket,
        sort_order: input.sort_order,
        is_active: input.is_active,
      }
      const { data, error: insErr } = await supabase
        .from("event_ticket_types")
        .insert(insert)
        .select()
        .single()
      if (insErr) {
        setError(insErr.message)
        return null
      }
      await fetchTicketTypes()
      return data as EventTicketTypeRow
    },
    [orgId, fetchTicketTypes]
  )

  const deleteTicketType = useCallback(
    async (id: string): Promise<boolean> => {
      if (!orgId) return false
      const { error: delErr } = await supabase
        .from("event_ticket_types")
        .delete()
        .eq("id", id)
        .eq("org_id", orgId)
      if (delErr) {
        setError(delErr.message)
        return false
      }
      await fetchTicketTypes()
      return true
    },
    [orgId, fetchTicketTypes]
  )

  const reorderTicketTypes = useCallback(
    async (orderedIds: string[]): Promise<boolean> => {
      if (!orgId) return false
      for (let i = 0; i < orderedIds.length; i++) {
        const { error: upErr } = await supabase
          .from("event_ticket_types")
          .update({ sort_order: i })
          .eq("id", orderedIds[i])
          .eq("org_id", orgId)
        if (upErr) {
          setError(upErr.message)
          return false
        }
      }
      await fetchTicketTypes()
      return true
    },
    [orgId, fetchTicketTypes]
  )

  const countRegistrationsForTicketType = useCallback(
    async (ticketTypeId: string): Promise<number> => {
      if (!orgId) return 0
      const { count, error: cErr } = await supabase
        .from("event_registrations")
        .select("*", { count: "exact", head: true })
        .eq("ticket_type_id", ticketTypeId)
        .eq("org_id", orgId)
        .in("status", ["confirmed", "waitlisted"])
      if (cErr) return 0
      return count ?? 0
    },
    [orgId]
  )

  return {
    ticketTypes,
    loading,
    error,
    fetchTicketTypes,
    saveTicketType,
    deleteTicketType,
    reorderTicketTypes,
    countRegistrationsForTicketType,
  }
}
